/**
 * ✅ FASE 11: Cache Service com IndexedDB
 * Sistema de cache inteligente para dados offline-first
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface CacheEntry<T = any> {
  key: string;
  data: T;
  timestamp: number;
  version: number;
  expiresAt?: number;
}

interface TDDCacheDB extends DBSchema {
  cache: {
    key: string;
    value: CacheEntry;
    indexes: { 'by-timestamp': number };
  };
  syncQueue: {
    key: string;
    value: {
      id: string;
      operation: 'create' | 'update' | 'delete';
      table: string;
      data: any;
      timestamp: number;
    };
  };
}

class CacheService {
  private db: IDBPDatabase<TDDCacheDB> | null = null;
  private readonly DB_NAME = 'tdd-cache';
  private readonly DB_VERSION = 1;
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  private isOnline = navigator.onLine;

  constructor() {
    this.initDB();
    this.setupOnlineListener();
  }

  private async initDB() {
    try {
      this.db = await openDB<TDDCacheDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade(db) {
          // Store de cache principal
          if (!db.objectStoreNames.contains('cache')) {
            const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
            cacheStore.createIndex('by-timestamp', 'timestamp');
          }

          // Store de fila de sincronização
          if (!db.objectStoreNames.contains('syncQueue')) {
            db.createObjectStore('syncQueue', { keyPath: 'id' });
          }
        },
      });

      console.log('✅ Cache IndexedDB inicializado');
    } catch (error) {
      console.error('❌ Erro ao inicializar IndexedDB:', error);
    }
  }

  private setupOnlineListener() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('🟢 Online - iniciando sincronização');
      this.syncPendingOperations();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('🔴 Offline - usando cache local');
    });
  }

  /**
   * Salvar dados no cache
   */
  async set<T>(key: string, data: T, ttl?: number): Promise<void> {
    if (!this.db) await this.initDB();
    if (!this.db) return;

    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: Date.now(),
      version: 1,
      expiresAt: ttl ? Date.now() + ttl : undefined,
    };

    try {
      await this.db.put('cache', entry as CacheEntry);
      await this.checkCacheSize();
    } catch (error) {
      console.error('Erro ao salvar cache:', error);
    }
  }

  /**
   * Recuperar dados do cache
   */
  async get<T>(key: string): Promise<T | null> {
    if (!this.db) await this.initDB();
    if (!this.db) return null;

    try {
      const entry = await this.db.get('cache', key);
      
      if (!entry) return null;

      // Verificar expiração
      if (entry.expiresAt && entry.expiresAt < Date.now()) {
        await this.delete(key);
        return null;
      }

      return entry.data as T;
    } catch (error) {
      console.error('Erro ao ler cache:', error);
      return null;
    }
  }

  /**
   * Verificar se item existe no cache e está válido
   */
  async has(key: string): Promise<boolean> {
    const data = await this.get(key);
    return data !== null;
  }

  /**
   * Deletar item do cache
   */
  async delete(key: string): Promise<void> {
    if (!this.db) return;
    try {
      await this.db.delete('cache', key);
    } catch (error) {
      console.error('Erro ao deletar cache:', error);
    }
  }

  /**
   * Limpar cache expirado
   */
  async clearExpired(): Promise<void> {
    if (!this.db) return;

    try {
      const tx = this.db.transaction('cache', 'readwrite');
      const store = tx.objectStore('cache');
      const index = store.index('by-timestamp');
      
      const entries = await index.getAll();
      const now = Date.now();

      for (const entry of entries) {
        if (entry.expiresAt && entry.expiresAt < now) {
          await store.delete(entry.key);
        }
      }

      await tx.done;
    } catch (error) {
      console.error('Erro ao limpar cache expirado:', error);
    }
  }

  /**
   * Limpar todo o cache
   */
  async clear(): Promise<void> {
    if (!this.db) return;
    try {
      await this.db.clear('cache');
      console.log('✅ Cache limpo');
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
    }
  }

  /**
   * Verificar e limitar tamanho do cache
   */
  private async checkCacheSize(): Promise<void> {
    if (!this.db) return;

    try {
      const estimate = await navigator.storage?.estimate();
      if (!estimate?.usage) return;

      if (estimate.usage > this.MAX_CACHE_SIZE) {
        // Remover entradas mais antigas
        const tx = this.db.transaction('cache', 'readwrite');
        const store = tx.objectStore('cache');
        const index = store.index('by-timestamp');
        
        const entries = await index.getAll();
        const sortedEntries = entries.sort((a, b) => a.timestamp - b.timestamp);
        
        // Remover 20% mais antigos
        const toRemove = Math.floor(sortedEntries.length * 0.2);
        for (let i = 0; i < toRemove; i++) {
          await store.delete(sortedEntries[i].key);
        }

        await tx.done;
        console.log(`🧹 Cache reduzido: ${toRemove} itens removidos`);
      }
    } catch (error) {
      console.error('Erro ao verificar tamanho do cache:', error);
    }
  }

  /**
   * Adicionar operação à fila de sincronização
   */
  async queueOperation(
    operation: 'create' | 'update' | 'delete',
    table: string,
    data: any
  ): Promise<void> {
    if (!this.db) await this.initDB();
    if (!this.db) return;

    const id = `${table}-${operation}-${Date.now()}-${Math.random()}`;
    
    try {
      await this.db.put('syncQueue', {
        id,
        operation,
        table,
        data,
        timestamp: Date.now(),
      });

      console.log(`📝 Operação enfileirada: ${operation} em ${table}`);
      
      // Se online, sincronizar imediatamente
      if (this.isOnline) {
        this.syncPendingOperations();
      }
    } catch (error) {
      console.error('Erro ao enfileirar operação:', error);
    }
  }

  /**
   * Sincronizar operações pendentes
   */
  private async syncPendingOperations(): Promise<void> {
    if (!this.db || !this.isOnline) return;

    try {
      const operations = await this.db.getAll('syncQueue');
      
      if (operations.length === 0) return;

      console.log(`🔄 Sincronizando ${operations.length} operações pendentes`);

      // Processar operações em ordem
      for (const op of operations) {
        try {
          // Aqui você implementaria a lógica real de sincronização com Supabase
          // Por exemplo: await supabase.from(op.table)[op.operation](op.data)
          
          // Remover da fila após sucesso
          await this.db.delete('syncQueue', op.id);
          console.log(`✅ Operação sincronizada: ${op.operation} em ${op.table}`);
        } catch (error) {
          console.error(`❌ Erro ao sincronizar operação ${op.id}:`, error);
          // Manter na fila para tentar depois
        }
      }

      console.log('✅ Sincronização concluída');
    } catch (error) {
      console.error('Erro ao sincronizar operações:', error);
    }
  }

  /**
   * Obter estatísticas do cache
   */
  async getStats(): Promise<{
    cacheSize: number;
    queueSize: number;
    storageUsage?: number;
    storageQuota?: number;
  }> {
    if (!this.db) await this.initDB();
    if (!this.db) return { cacheSize: 0, queueSize: 0 };

    try {
      const cacheSize = await this.db.count('cache');
      const queueSize = await this.db.count('syncQueue');
      const estimate = await navigator.storage?.estimate();

      return {
        cacheSize,
        queueSize,
        storageUsage: estimate?.usage,
        storageQuota: estimate?.quota,
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      return { cacheSize: 0, queueSize: 0 };
    }
  }

  /**
   * Verificar se está online
   */
  getOnlineStatus(): boolean {
    return this.isOnline;
  }
}

export const cacheService = new CacheService();
